import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Send, Languages } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { API } from "../App";
import { LANGUAGES } from "../lib/languages";

/** Aperçu fidèle de l'e-mail d'invitation (langue de la fiche patient, modifiable) avant envoi. */
const InvitePreviewDialog = ({ open, onOpenChange, patient, onConfirm, sending }) => {
  const [lang, setLang] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setLang(patient?.language || "fr"); setPreview(null); setError(""); } }, [open, patient?.language]);

  useEffect(() => {
    if (!open || !lang || !patient?.user_id) return;
    setPreview(null);
    axios.get(`${API}/therapist/patients/${patient.user_id}/invite/preview`, { params: { lang }, withCredentials: true })
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Impossible de charger l'aperçu"));
  }, [open, lang, patient?.user_id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invite-preview-dialog">
        <DialogHeader><DialogTitle className="font-heading">Aperçu de l'e-mail d'invitation</DialogTitle></DialogHeader>
        <div className="text-sm space-y-1 border rounded-lg p-3 bg-muted/30">
          <p><span className="text-muted-foreground w-16 inline-block">De :</span> {preview?.sender || "…"}</p>
          <p data-testid="invite-preview-to"><span className="text-muted-foreground w-16 inline-block">À :</span> {patient?.email}</p>
          <p data-testid="invite-preview-subject"><span className="text-muted-foreground w-16 inline-block">Objet :</span> <strong>{preview?.subject || "…"}</strong></p>
          <label className="flex items-center gap-2 pt-1">
            <Languages className="w-4 h-4 text-muted-foreground" /><span className="text-muted-foreground">Langue :</span>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-sm" data-testid="invite-preview-lang">
              {LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.label}{l.id === (patient?.language || "fr") ? " (fiche patient)" : ""}</option>)}
            </select>
          </label>
        </div>
        {error && <p className="text-sm text-destructive" data-testid="invite-preview-error">{error}</p>}
        {preview ? (
          <iframe title="Aperçu de l'invitation" srcDoc={preview.html} sandbox="" className="w-full h-[50vh] border rounded-lg bg-[#f1f5f9]" data-testid="invite-preview-body" />
        ) : !error && <div className="h-[50vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        <p className="text-xs text-muted-foreground">Le lien affiché est une démonstration : le lien réel, valable 7 jours et à usage unique, est généré à l'envoi.</p>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="invite-preview-cancel-btn">Annuler</Button>
          <Button onClick={() => onConfirm(lang)} disabled={sending || !preview} className="gap-1.5" data-testid="invite-preview-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sending ? "Envoi..." : "Envoyer l'invitation"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvitePreviewDialog;
